import { Component } from 'react'
import _ from 'lodash'

import PropTypes from 'prop-types'
import update from 'immutability-helper'
import cx from 'classnames'
import styles from './index.css'
import { S3Upload } from '@vzmi/ec_file_uploader_sdk'
import withUploadModule from '../HOC/withUploadModule'
import {
  IMAGE_UPLOADER_MAX_FILE
} from '../../Shared'
import ImageDragger from './ImageDragger'
import UploadList from './UploadList'
import arrayMove from 'array-move'
import {
  getIsEmptyFileList,
  filterFileListByError,
  setDimension,
  setUidAndMimeType,
  verifyMismatchedFileMimeType
} from '../../../../lib/uploaderUtil'
import {
  MAIN_IMAGE_ERROR_TEXT,
  getValidMainAndReviewImages,
  REVIEW_IMAGE_ERROR_TEXT,
  organizeImageFileList,
  getUploadableFileList,
  verifyUploadImage,
  sortImageFileListByOrder,
  IMAGE_RECOMMEND_DIMENSION_TEXT,
  chunkUploaderDataByDimension,
  getUploadableSlot
} from './ImageUploaderHelper'
import { Toast } from '@vzmi/shp_shared_compo'
import { TOAST_DURATION } from '../../../../constants/proposalTypes'
import { ANT_UPLOAD_STATUS } from '../../../../constants/globalTypes'

export const MAIN_IMAGE_SPEC = {
  POSITION: 0,
  ORDER: 1
}
export const REVIEW_IMAGE_SPEC = {
  POSITION: 1,
  ORDER: 2
}

export const Warn = ({ className, message }) => {
  return (
    <p className={cx(styles.Warn, className)}>
      {message}
    </p>
  )
}

const allowMovingImageToNextIndex = (file, index, nextFileList) => {
  const validLargeImagesOfNextFiles = getValidMainAndReviewImages(nextFileList)
  const validLargeImages = getValidMainAndReviewImages([file])
  const isValidLargeImage = !_.isEmpty(validLargeImages)
  let errorMessage
  // 大圖往後拖，如果遞補的還是本身 必須提示，但允許繼續更換位置
  if (index > 1 && _.includes(_.map(validLargeImagesOfNextFiles, 'uid'), file.uid)) {
    errorMessage = `欲遞補主圖區的圖片必須為${IMAGE_RECOMMEND_DIMENSION_TEXT}`
    Toast.warning(errorMessage, { duration: 3 })
  } else if (index === MAIN_IMAGE_SPEC.POSITION && !isValidLargeImage) {
    errorMessage = MAIN_IMAGE_ERROR_TEXT
    Toast.error(errorMessage, { duration: 3 })
    return false
  } else if (index === REVIEW_IMAGE_SPEC.POSITION && !isValidLargeImage) {
    errorMessage = REVIEW_IMAGE_ERROR_TEXT
    Toast.error(errorMessage, { duration: 3 })
    return false
  }
  return true
}

class ImageUploader extends Component {
  state = {
    uploaderOptions: {},
    maxFile: this.props.maxFile,
    imageFileList: [],
    error: null
  }

  componentDidMount = () => {
    this.props.setDefaultFileList(this.props.defaultFileList)
    this.props.setUploaderOptions(this.props)
  }

  componentDidUpdate (prevProps) {
    const nextDefaultFileList = this.props.defaultFileList
    const currentFileList = prevProps.defaultFileList
    if (_.isEmpty(currentFileList) && !_.isEqual(currentFileList, nextDefaultFileList)) {
      this.props.setDefaultFileList(nextDefaultFileList)
    }
    if (prevProps.fileList !== this.props.fileList) {
      const { fileList } = this.props
      // 如果沒有任何合法圖片則清空圖片區
      const {
        mainAndReviewImages,
        otherSizeImages
      } = chunkUploaderDataByDimension(fileList)
      let imageFileList = organizeImageFileList(fileList)
      if (_.isEmpty(mainAndReviewImages) && _.isEmpty(otherSizeImages)) {
        imageFileList = []
      }
      // 在 fileList 有變更時，確認 error 更新
      const error = _.get(
        _.find(imageFileList, (file = {}) => file.status === ANT_UPLOAD_STATUS.ERROR),
        'error',
        null
      )
      this.setState(() => update(this.state, {
        imageFileList: { $set: imageFileList },
        error: { $set: error }
      }))
    }
  }

  resetErrorState = (imageFileList = []) => {
    this.setState({
      error: null
    })
  }

  resetFileList = (imageFileList = []) => {
    this.setState({
      error: null
    })
    this.props.updateFileList(imageFileList)
  }

  handleBeforeUpload = async (file, fileList) => {
    const { imageFileList } = this.state
    try {
      const preprocessedUploadFiles = await setUidAndMimeType(fileList).then(setDimension)
      const allowUploadFileList = getUploadableFileList(imageFileList, preprocessedUploadFiles)
      const allowUploadFile = _.find(allowUploadFileList, { uid: file.uid }) || {}
      const isFileAllowedUpload = _.some(fileList, { uid: allowUploadFile.uid })
      const {
        otherSizeImages
      } = chunkUploaderDataByDimension(preprocessedUploadFiles)
      /* eslint-disable-next-line */
      const [ mainSlot, otherSlot ] = getUploadableSlot(imageFileList)
      // Limit the number of uploaded files部分圖片尺寸/格式有誤
      const pendingFileListCount = _.size(fileList) + _.size(filterFileListByError(imageFileList))
      let errors = []
      let isReject = false
      if (_.findIndex(fileList, file) === 0) {
        // 只出現一次超出上限錯誤訊息
        if (pendingFileListCount > IMAGE_UPLOADER_MAX_FILE) {
          errors.push('圖片超過數量上限，只保留有效張數')
        }
        if (_.size(otherSizeImages) > otherSlot) {
          errors.push('圖片超過數量上限，只保留有效張數')
        } else {
          errors = _.reduce(preprocessedUploadFiles, (acc, f) => {
            const mimeTypeError = verifyMismatchedFileMimeType(file)
            if (mimeTypeError) {
              acc.push(mimeTypeError)
              return acc
            }
            const { error: errorMessage } = verifyUploadImage(f)
            if (errorMessage) {
              if (f.uid === file.uid) { isReject = true }
              acc.push(errorMessage)
            }
            return acc
          }, errors)
        }
        _.each(_.uniq(errors), e => {
          Toast.error(e, { duration: TOAST_DURATION })
        })
      }
      return new Promise((resolve, reject) => {
        return isFileAllowedUpload && !isReject ? resolve(file) : reject(file)
      })
    } catch (e) {
      console.log('handleBeforeUpload error', e)
      Toast.error(e.message, { duration: TOAST_DURATION })
      throw e
    }
  }

  handleChange = (info) => {
    this.props.handleChange(info)
  }

  handleError = (error) => {
    this.setState({
      error
    })
  }

  handleSortEnd = ({ oldIndex, newIndex }) => {
    const imageFileList = this.state.imageFileList
    if (oldIndex !== newIndex) {
      const moveFile = _.get(imageFileList, oldIndex, {})
      const nextItems = arrayMove(imageFileList, oldIndex, newIndex)
      const isAllowMoving = allowMovingImageToNextIndex(moveFile, newIndex, nextItems)
      if (isAllowMoving) {
        this.props.updateFileList(sortImageFileListByOrder(nextItems))
      }
    }
  }

  triggerUpload = () => {
    this.uploadButton.click()
  }

  customRequest = (props) => {
    // reset error state
    this.resetErrorState(this.state.imageFileList)
    const options = this.props.getDefaultS3Options(props)
    // actaully start uploading to S3
    // eslint-disable-next-line
    new S3Upload({
      ...options,
      onError: (error, file) => {
        // Add error state to file and return it
        options.onError(error, file)
        this.handleError(error)
      }
    })
  }

  getDragUploaderProps = () => {
    const { multiple, accept } = this.props
    return {
      key: 'drag',
      name: 'file',
      disabled: false,
      accept,
      component: 'div',
      multiple,
      onChange: this.handleChange,
      customRequest: this.customRequest,
      showUploadList: false,
      beforeUpload: this.handleBeforeUpload
    }
  }

  renderDragUploader = () => {
    const { imageFileList } = this.state
    const { disabled = false } = this.props
    const isEmptyList = getIsEmptyFileList(imageFileList)
    const props = {
      ...this.getDragUploaderProps(),
      className: cx(styles.dragUploader, { [styles.hide]: !isEmptyList }),
      disabled,
      fileList: imageFileList
    }

    return (
      <ImageDragger {...props}>
        <button
          type='button'
          data-role='hidden-upload-button'
          style={{ display: 'none' }}
          ref={e => (this.uploadButton = e)}
        />
      </ImageDragger>
    )
  }

  renderUploadContent = () => {
    const { disabled } = this.props
    const { imageFileList, maxFile } = this.state
    const isEmptyList = getIsEmptyFileList(imageFileList)
    // helperClass is to attach to dragging element
    // setting distance to allow click event inside element
    // ref: https://github.com/clauderic/react-sortable-hoc/issues/368
    const inlineListProps = {
      fileList: imageFileList,
      className: styles.uploadInlineList,
      onItemRemove: this.props.removeFile,
      axis: 'xy',
      distance: 1,
      disabled,
      helperClass: 'is-dragging',
      onSortEnd: this.handleSortEnd,
      triggerUpload: this.triggerUpload,
      dragUploaderProps: this.getDragUploaderProps()
    }
    return (
      <div className={styles.uploadContent}>
        {
          !isEmptyList && (
            <UploadList
              key='upload'
              maxFile={maxFile}
              {...inlineListProps}
            />
          )
        }
        {
          this.renderDragUploader()
        }
      </div>
    )
  }

  render () {
    const { error, maxFile } = this.state
    const dataRole = this.props['data-role']

    return (
      <div
        data-role={dataRole}
        className={cx(styles.ImageUploader, { [styles.error]: error })}
      >

        {
          this.renderUploadContent()
        }
        <div
          key='panel'
          className={styles.panel}
        >
          <ul key='message'>
            <li
              key='1'
              className={styles.info}
            >
              {`圖片上限：最多${maxFile}張，`}
              <span className={styles.em}>
                {`必含2張主圖（${IMAGE_RECOMMEND_DIMENSION_TEXT}）`}
              </span>
            </li>
            <li
              key='2'
              className={styles.info}
            >
              {`圖片尺寸：均為${IMAGE_RECOMMEND_DIMENSION_TEXT}(建議)`}
            </li>
            <li
              key='3'
              className={styles.info}
            >
              圖片格式：JPG、JPEG、PNG
            </li>
            <li
              key='4'
              className={styles.info}
            >
              特別提醒：第2張主圖需為白底圖(去背)，無浮水印/logo/鑲框/文字等修飾物件，以符合Google PLA規範，消費者可藉關鍵字搜尋到達賣場增加曝光
            </li>
          </ul>
          {error && (
            <Warn
              key='warn'
              message={error}
            />
          )}
        </div>
      </div>
    )
  }
}

ImageUploader.defaultProps = {
  resetFileListOnUpload: false,
  maxFile: IMAGE_UPLOADER_MAX_FILE,
  multiple: true,
  disabled: false,
  defaultFileList: [],
  'data-role': 'image-uploader'
}

ImageUploader.propTypes = {
  accept: PropTypes.string,
  maxFile: PropTypes.number,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  resetFileListOnUpload: PropTypes.bool,
  defaultFileList: PropTypes.oneOfType([PropTypes.any, PropTypes.object, PropTypes.array]),
  actions: PropTypes.object,
  onItemRemove: PropTypes.func,
  onUpdateFile: PropTypes.func,
  onFinish: PropTypes.func
}

export default withUploadModule(ImageUploader)
